import { cn } from '@/shared/helpers/classnames'
import { type DataAttributes } from '@/shared/types/html'
import { type Size } from '@/shared/types/size'
import { WithIcon } from '@/shared/ui/icon'
import { type TooltipProps, useToolTip } from '@/shared/ui/tooltip'

import { type FC, HTMLAttributes } from 'react'

import { IconProps } from '../icon/icon.type'

type TagsSize = Exclude<Size, 'xl'>

/**
 * Props for the Tag component
 */
export type TagProps = {
  /** Visual style */
  variant?: 'soft' | 'full'
  /** Whether the tag is disabled */
  disabled?: boolean
  /** Button size */
  size?: TagsSize
  /** Optional icon component (from react-bootstrap-icons) */
  icon?: IconProps
  /** Button label content */
  label?: string
  /** Action on click */
  onClick?: HTMLAttributes<HTMLDivElement>['onClick']
  /** Tooltip properties */
  tooltip?: TooltipProps
  /** Whether the label should be truncated */
  truncate?: boolean
} & DataAttributes

/**
 * The Tag component displays a collection of fund badges with optional label and currency symbols.
 * Each fund is rendered as a badge with customizable style variants.
 *
 * @param {TagProps} props - Tag component props
 * @returns {JSX.Element}
 */
export const Tag: FC<TagProps> = ({
  size = 'md',
  variant = 'full',
  disabled = false,
  truncate = false,
  icon,
  label,
  onClick,
  tooltip,
  ...dataProps
}) => {
  const tooltipProps = useToolTip(tooltip ?? {})

  const iconNode = useIcon({
    ...icon,
    size,
  })

  const isClickable = !disabled && !!onClick

  return (
    <div
      {...tooltipProps}
      {...dataProps}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled}
      className={cn('badge tag', {
        'badge-soft': variant === 'soft',
        'badge-primary': variant === 'full' && !disabled,
        'badge-disabled': disabled,
        'badge-xs': size === 'xs',
        'badge-sm': size === 'sm',
        'badge-md': size === 'md',
        'badge-lg': size === 'lg',
        'cursor-pointer': onClick && !disabled,
      })}
    >
      <WithIcon icon={iconNode} position={icon?.position}>
        <span className={cn({ 'max-w-24 truncate': truncate })}>{label}</span>
      </WithIcon>
    </div>
  )
}

/**
 * Returns the icon node for the Tag component based on the provided size.
 *
 * @param {IconProps & Pick<TagProps, 'size'>} params - Icon component and size
 * @returns {JSX.Element | undefined} Icon node or undefined
 */
const useIcon = ({ component: IconComponent, size }: IconProps & { size: TagsSize }) => {
  const iconSizeBySize: Record<TagsSize, number> = {
    lg: 16,
    md: 14,
    sm: 12,
    xs: 10,
  }

  const iconSize = iconSizeBySize[size]
  return IconComponent && <IconComponent size={iconSize} />
}
